import { useState, useEffect } from 'react';
import styled from 'styled-components';
import moment from 'moment';
import { displayDate } from 'utils/date';
import { useDispatch, useSelector } from 'react-redux';
import { CSVLink } from 'react-csv';
import { Tooltip } from 'ui/tooltip';
import {
  selectIsContextualizedMetricsFeatureFlagOn,
  selectIsMetricDefinitionsFeatureFlagOn,
  selectIsMIApplyButtonFeatureFlagOn,
  selectIsSegmentCardinalityImprovementsFeatureFlagOn,
} from 'core/userContext';
import { Export16, WarningFill } from 'ui/icons';
import { NoResult } from 'ui/illustrations';
import { Table } from 'ui/table/Table';
import { selectUserMappings } from 'modules/analytics/selectors';
import {
  useCampaignAndMetricFiltersContext,
  useTabSpecificFiltersContext,
  useTimeFiltersContext,
} from 'modules/messagingInsights/context';
import { useMetricsContext } from 'modules/productAnalytics/context';
import {
  METRIC_VALUES_TO_LABELS,
  HIGH_LEVEL_VALUES_TO_LABELS,
} from 'constants/metricValuesToLabels';

import { DefinitionWrapper } from 'modules/metricDefinitions';
import { GroupButton } from 'ui/buttonGroup';
import { SecondaryButton } from 'ui/button';
import { BasicFilterSelect } from 'ui/selects';
import { spacing, colors, body1Bold } from 'ui/theme';
import {
  NoSegmentSelectedMessage,
  SelectSegmentMessage,
  NoMatchingSegmentsMessage,
  CheckFiltersMessage,
} from 'constants/messageCopies';
import { addPositiveToast } from 'modules/toastContainer';
import {
  EMAIL_LIST_ID_COL,
  SEGMENT_CARDINALITY_LIMIT,
  SEGMENT_COL,
} from 'modules/productAnalytics/constants';
import { useSegmentSortByParam } from 'modules/productAnalytics/hooks';
import {
  parseUserMappingsForDropdown,
  parseColsToCSVHeaders,
  sortBySelectionOrder,
} from 'modules/productAnalytics/utils';
import { TooltipCardinalityTrigger } from 'modules/productAnalytics/Styled';
import { isEmpty } from '@iterable/utils';
import { ColumnData } from 'modules/messagingInsights/types';
import { AlignTypes } from 'ui/tableHeader';
import { TruncatedHeaderCell } from '../TruncatedHeaderCell';
import { Segment as SegmentIcon } from '../../assets/segment';
import {
  selectError,
  selectSegmentData,
  selectSegmentsLoading,
  selectAppliedCrossMetrics,
  selectAppliedMediums,
} from '../../selectors';
import { SegCell } from '../../Styled';
import { fetchSegmentsV2Request } from '../../actions';
import { colorsByMedium, FETCH_ERRORS as e, ListIdSegs } from '../../constants';
import {
  getMetricColumnsForSegment,
  getLoadingRows,
  parseSelectedMetrics,
  getUserFriendlyMetricLabel,
  stringifyArr,
  stringifyVals,
} from '../../utils';
import { NoDataMessage } from '../NoDataMessage';
import { SegmentQueryData, UserMappings } from './types';
import { getCSVdata, getSegmentCount, parseSegmentResp, sortUndefinedRows } from './utils';
import {
  SelectWrapper,
  SegmentControls,
  ButtonGroupWrapper,
  ExportWrapper,
  StyledButtonGroup,
  SegmentBody,
  TableWrapper,
  StyledHeader,
  StyledRows,
  MediumCircle,
  SegmentCard,
} from './Styled';
import { Banners } from './Banners';

const StyledWarningIcon = styled(WarningFill)`
  margin-right: ${spacing.quarter};
`;

const StyledWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.half};
  > span {
    ${body1Bold}
  }
`;

const SEGMENT_LIMIT_ERROR =
  'Search results exceed limit. Please refine your search criteria and try again.';
const copy = {
  limitError: {
    title: 'Search results exceed limit',
    subtitle: 'Please refine your search criteria and try again.',
  },
  fetchError: {
    title: "Sorry we couldn't load this page",
    subtitle: 'Try refreshing the page. If this keeps happening, let us know.',
  },
};

type AudienceProps = {
  campaignIds: number[];
};

export const Audience = ({ campaignIds = [] }: AudienceProps) => {
  const dispatch = useDispatch();
  const isSegCardinalityFFOn = useSelector(selectIsSegmentCardinalityImprovementsFeatureFlagOn);
  const metricDefFFOn = useSelector(selectIsMetricDefinitionsFeatureFlagOn);
  const contextualizedMetricsFFOn = useSelector(selectIsContextualizedMetricsFeatureFlagOn);

  // Time-based filters
  const { dateParams } = useTimeFiltersContext();
  const { startTime, endTime } = dateParams;
  const start = startTime?.toISOString() || '';
  const end = endTime?.toISOString() || '';

  // Campaign and metric-based filters
  const {
    selectedMediums,
    selectedCrossMetrics: metricSelectionOrder,
    mediumMetrics,
    overallMetrics,
    MediumFilteredAMs,
    MediumFilteredHLMs,
    previousAudienceFilters,
    setPreviousAudienceFilters,
  } = useCampaignAndMetricFiltersContext();

  // Metrics
  const { analyticsMetrics, analyticsMetricsValueTypes, highLevelMetricsValueTypes } =
    useMetricsContext();

  // Segment states
  // @ts-ignore
  const userMappings: UserMappings = useSelector(selectUserMappings);
  const segmentOptions = parseUserMappingsForDropdown(userMappings);
  const loading = useSelector(selectSegmentsLoading);
  const {
    selectedSegment,
    setSelectedSegment,
    audienceSortedBy: sortedBy,
    setAudienceSortedBy: setSortedBy,
    audienceSortDirection: sortDirection,
    setAudienceSortDirection: setSortDirection,
  } = useTabSpecificFiltersContext();

  const [segmentIsDate, setSegmentIsDate] = useState(false);
  const [segmentInterval, setSegmentInterval] = useState('1M');
  const segmentData = useSelector(selectSegmentData);
  const unsupportedMetrics = segmentData?.UnsupportedMetrics || [];
  const isMIApplyButtonFFOn = useSelector(selectIsMIApplyButtonFeatureFlagOn);
  const appliedCrossMetrics: string[] = useSelector(selectAppliedCrossMetrics);
  const appliedMediums: string[] = useSelector(selectAppliedMediums);
  const supportedMetrics = metricSelectionOrder.filter(
    (metric) => !unsupportedMetrics.includes(METRIC_VALUES_TO_LABELS[metric]),
  );

  const isHighCardinality = isSegCardinalityFFOn && selectedSegment?.name === 'domain';
  const sortByParam = useSegmentSortByParam(sortedBy, supportedMetrics[0]);
  const sortByParamVal =
    METRIC_VALUES_TO_LABELS?.[sortByParam.sortByMediumMetric?.name || ''] ||
    HIGH_LEVEL_VALUES_TO_LABELS[sortByParam.sortByOverallMetric?.enumName || ''] ||
    '';
  const [cardinalitySortBy, setCardinalitySortBy] = useState(sortByParamVal);
  const displayedSortedBy = isHighCardinality ? cardinalitySortBy : sortedBy;

  const selectedMeds = isMIApplyButtonFFOn ? appliedMediums : selectedMediums;

  const metricSelOrder = isMIApplyButtonFFOn ? appliedCrossMetrics : metricSelectionOrder;

  // Prepare Segment Table Data
  const formatSegmentName = (segment: string, type: string | undefined, medium: string) => {
    const segName =
      type === 'date' ? displayDate(moment.unix(parseInt(segment, 10) / 1000)) : segment;
    return medium === 'All' ? (
      <b>{segName}</b>
    ) : (
      <>
        <MediumCircle color={colorsByMedium[medium]}></MediumCircle>
        {segName} ({medium})
      </>
    );
  };

  const BASE_SEG_HEADER_COLS = [
    {
      name: 'Segment by medium',
      value: SEGMENT_COL,
      align: 'flex-start' as AlignTypes,
      RowCell: ({ row }: any) => (
        <SegCell>
          <TruncatedHeaderCell
            id="Segment"
            name={formatSegmentName(row.Segment, selectedSegment?.type, row.medium)}
            textOnly
          />
        </SegCell>
      ),
    },
  ];

  const EmailListName = {
    name: 'Email list name',
    value: EMAIL_LIST_ID_COL,
    align: 'flex-start' as AlignTypes,
    // @ts-ignore
    RowCell: ({ row }: any) => <SegCell metric>{row.EmailListName}</SegCell>,
  };

  const parsedSegmentData = parseSegmentResp(
    segmentData,
    selectedMeds,
    metricSelOrder,
    selectedSegment,
    displayedSortedBy,
    unsupportedMetrics,
    analyticsMetrics,
  );

  const segmentCount = getSegmentCount(segmentData);
  const showHighCardinalityFeedback = isHighCardinality && segmentCount > SEGMENT_CARDINALITY_LIMIT;
  const sortedRows = sortUndefinedRows(parsedSegmentData, displayedSortedBy, sortDirection).filter(
    (row) => (isHighCardinality ? row?.Segment !== 'Not Included In Results' : true),
  );

  const columns: ColumnData[] = [...BASE_SEG_HEADER_COLS];
  if (ListIdSegs.has(selectedSegment?.name || '')) {
    columns.push(
      selectedSegment.name === 'emailListIds'
        ? EmailListName
        : { ...EmailListName, name: 'List name' },
    );
  }
  const metricSet: Set<string> = new Set();
  Object.values(parsedSegmentData).forEach((seg) => {
    Object.keys(seg).forEach((segMetric) => {
      if (!['Segment', 'EmailListName', 'medium', 'id'].includes(segMetric)) {
        metricSet.add(segMetric);
      }
    });
  });
  const metricArray: string[] = Array.from(metricSet);

  // sort the metrics provided from BE based on the user's selection order (preserved in metricSelectionOrder)
  const selectionOrder = metricSelOrder.map((metricName) => getUserFriendlyMetricLabel(metricName));
  const sortedMetricArray = sortBySelectionOrder(selectionOrder, metricArray) as string[];
  const ValueTypes = { ...analyticsMetricsValueTypes, ...highLevelMetricsValueTypes };
  columns.push(...getMetricColumnsForSegment(sortedMetricArray, ValueTypes));

  // Empty + Error state logic
  const errors = useSelector(selectError) || {};
  const showErrorState = Object.keys(errors).includes(e.FETCH_SEGMENTSV2);
  const isLimitError = errors[e.FETCH_SEGMENTSV2] === SEGMENT_LIMIT_ERROR;
  const noDataMessageTitle = !selectedSegment?.name
    ? NoSegmentSelectedMessage
    : NoMatchingSegmentsMessage;
  const noDataMessageSubtitle = !selectedSegment?.name ? SelectSegmentMessage : CheckFiltersMessage;
  const showTable =
    !isEmpty(selectedSegment) && !showErrorState && (loading || sortedRows?.length > 0);

  // Fetch segment data
  const queryData: SegmentQueryData = {
    campaignIds,
    startTime: start,
    endTime: end,
    mediumMetrics,
    overallMetrics,
    mediums: selectedMediums,
    userProperty: selectedSegment?.name,
    showUnsupportedMetrics: true,
    ...(isSegCardinalityFFOn ? sortByParam : {}),
  };

  const parsedAppliedMetrics = parseSelectedMetrics(appliedCrossMetrics);

  const appliedOverallMetrics = parsedAppliedMetrics
    .filter((val) => MediumFilteredHLMs.has(val.name))
    .map((val) => ({ enumName: val.name }));
  const appliedMediumMetrics = parsedAppliedMetrics.filter((val) =>
    MediumFilteredAMs.has(val.name),
  );

  const appliedMediumMetricsStr = stringifyArr(appliedMediumMetrics);
  const appliedOverallMetricsStr = stringifyArr(appliedOverallMetrics);

  // if segmentsV2 fails, mediumMetrics + overallMetrics are reset to point to new arrays with the same values
  // this triggers the useEffect hook to infinitely recall segmentsV2.  To prevent this, we stringify both as mediumMetricsStr + overallMetricsStr
  const mediumMetricsStr = stringifyArr(mediumMetrics);
  const overallMetricsStr = stringifyArr(overallMetrics);
  const campaignIdsStr = stringifyArr(campaignIds);

  const appliedQueryData: SegmentQueryData = {
    ...queryData,
    mediumMetrics: appliedMediumMetrics,
    overallMetrics: appliedOverallMetrics,
    mediums: selectedMeds,
  };

  const fetchData = (data: any) => {
    const audVals = stringifyVals([
      selectedSegment,
      segmentInterval,
      mediumMetricsStr,
      overallMetricsStr,
      appliedMediumMetricsStr,
      appliedOverallMetricsStr,
      campaignIdsStr,
      start,
      end,
    ]);
    if (audVals !== previousAudienceFilters) {
      setPreviousAudienceFilters(audVals);
      dispatch(fetchSegmentsV2Request(data));
    }
  };

  useEffect(() => {
    if (!isMIApplyButtonFFOn) {
      if (selectedSegment?.type === 'date') {
        queryData.interval = segmentInterval;
        setSegmentIsDate(true);
      } else {
        setSegmentIsDate(false);
      }

      if (selectedSegment?.name) {
        fetchData(queryData);
      }
    }
  }, [
    selectedSegment,
    segmentInterval,
    mediumMetricsStr,
    overallMetricsStr,
    campaignIds,
    start,
    end,
  ]);

  useEffect(() => {
    if (isMIApplyButtonFFOn) {
      setCardinalitySortBy(sortByParamVal);
      if (selectedSegment?.type === 'date') {
        appliedQueryData.interval = segmentInterval;
        setSegmentIsDate(true);
      } else {
        setSegmentIsDate(false);
      }

      if (selectedSegment?.name) {
        fetchData(appliedQueryData);
      }
    }
  }, [
    selectedSegment,
    segmentInterval,
    appliedMediumMetricsStr,
    appliedOverallMetricsStr,
    campaignIds,
    start,
    end,
  ]);

  useEffect(() => {
    if (isHighCardinality && sortedBy !== 'Segment') {
      if (!isMIApplyButtonFFOn) {
        if (selectedSegment?.type === 'date') {
          queryData.interval = segmentInterval;
          setSegmentIsDate(true);
        } else {
          setSegmentIsDate(false);
        }

        if (selectedSegment?.name) {
          fetchData(queryData);
        }
      } else {
        if (selectedSegment?.type === 'date') {
          appliedQueryData.interval = segmentInterval;
          setSegmentIsDate(true);
        } else {
          setSegmentIsDate(false);
        }

        if (selectedSegment?.name) {
          fetchData(queryData);
        }
      }
    }
  }, [sortedBy]);

  // Add metric definition tooltips to column headers
  const metricDefColumns = columns?.map((col) => ({
    ...col,
    name: <DefinitionWrapper>{col.name as string}</DefinitionWrapper>,
  }));

  const highCardinalityColumns = columns?.map((col) => ({
    ...col,
    name:
      col.value === cardinalitySortBy && col.value !== 'Segment' && showHighCardinalityFeedback ? (
        <Tooltip tooltipContent="Only sorting based on the first 250 records">
          <TooltipCardinalityTrigger>
            <StyledWarningIcon color={colors.purple90} /> {col.name as string}
          </TooltipCardinalityTrigger>
        </Tooltip>
      ) : (
        col.name
      ),
  }));

  useEffect(() => {
    setCardinalitySortBy(sortByParamVal);
  }, [selectedSegment]);

  useEffect(() => {
    // set type for url-provided segments
    if (!selectedSegment?.type) {
      const matchingSegment = segmentOptions.filter(
        (opt) =>
          opt.value?.name?.toLocaleLowerCase() === selectedSegment?.name?.toLocaleLowerCase(),
      )[0];
      const type = matchingSegment?.value?.type;
      if (type) {
        setSelectedSegment(matchingSegment?.value || { ...selectedSegment, type });
      }
    }
  }, [selectedSegment, segmentOptions]);

  return (
    <div>
      <Banners
        selectedSegment={selectedSegment}
        mediumMetrics={mediumMetrics}
        overallMetrics={overallMetrics}
        unsupportedMetrics={unsupportedMetrics}
        highCardinality={showHighCardinalityFeedback ? selectedSegment?.name : undefined}
      />
      <SegmentCard>
        <SegmentControls>
          <SelectWrapper>
            <StyledWrapper>
              <span>Showing the</span>
              <BasicFilterSelect
                onSelect={(vals) => {
                  const val = vals[0] || {};
                  setSelectedSegment(val);
                }}
                id="user-mappings"
                title="user-mappings"
                options={segmentOptions}
                placeholder={selectedSegment?.name || 'Select a segment to analyze...'}
              />
              <span>segment</span>
            </StyledWrapper>
          </SelectWrapper>
          <ButtonGroupWrapper>
            {segmentIsDate && (
              <StyledButtonGroup id="segment-intervals" defaultSelectedId={`opt-1M`} trackSelected>
                <GroupButton key={`opt-1w`} id={`opt-1w`} onSelect={() => setSegmentInterval('1w')}>
                  Weekly
                </GroupButton>
                <GroupButton key={`opt-1M`} id={`opt-1M`} onSelect={() => setSegmentInterval('1M')}>
                  Monthly
                </GroupButton>
                <GroupButton key={`opt-1q`} id={`opt-1q`} onSelect={() => setSegmentInterval('1q')}>
                  Quarterly
                </GroupButton>
              </StyledButtonGroup>
            )}
          </ButtonGroupWrapper>
          <ExportWrapper>
            {sortedRows?.length > 0 && (
              <CSVLink
                data={getCSVdata(
                  sortedRows,
                  segmentIsDate,
                  sortedMetricArray,
                  selectedSegment,
                  ValueTypes,
                )}
                headers={parseColsToCSVHeaders(columns)}
                onClick={() => dispatch(addPositiveToast('Segments data successfully exported'))}
                filename="segment_table.csv"
              >
                <SecondaryButton>
                  <Export16 />
                  Export segments table
                </SecondaryButton>
              </CSVLink>
            )}
          </ExportWrapper>
        </SegmentControls>
        <SegmentBody>
          {showTable && (
            <TableWrapper>
              <Table>
                <StyledHeader
                  columns={
                    metricDefFFOn && !contextualizedMetricsFFOn
                      ? metricDefColumns
                      : highCardinalityColumns
                  }
                  sortedBy={displayedSortedBy}
                  setSortedBy={(sort) => {
                    setSortedBy(sort);
                    setCardinalitySortBy(sort);
                  }}
                  sortDirection={sortDirection}
                  setSortDirection={setSortDirection}
                  isSticky
                />
                <StyledRows
                  rows={loading ? getLoadingRows(metricArray, 10) : sortedRows}
                  // @ts-ignore
                  columns={columns}
                />
              </Table>
            </TableWrapper>
          )}
          {!showTable &&
            (showErrorState ? (
              <NoDataMessage
                Icon={NoResult}
                title={isLimitError ? copy.limitError.title : copy.fetchError.title}
                subtitle={isLimitError ? copy.limitError.subtitle : copy.fetchError.subtitle}
                noSpacing
              />
            ) : (
              <NoDataMessage
                data-test="empty-segments-msg"
                Icon={SegmentIcon}
                title={noDataMessageTitle}
                subtitle={noDataMessageSubtitle}
                noSpacing
              />
            ))}
        </SegmentBody>
      </SegmentCard>
    </div>
  );
};
